/**
 * Fan-out of the app-wide email notification stream to the views that care.
 *
 * The stream is one connection owned by the authenticated shell
 * (`useEmailNotificationStream`); the inbox list and the email page subscribe
 * here instead of each opening their own. A module-level listener set rather
 * than store state, because these are events, not state: nothing should
 * re-render when one passes, and a late subscriber must not replay old ones.
 */
import { useEffect, useRef } from 'react';

import { useAppStore } from '@/app/stores/useAppStore';

/** Minimum gap between two incremental-poll callbacks for one consumer. */
export const EMAIL_UPDATE_DEBOUNCE_MS = 1000;

/** Receives one `email_updated` event: its email id, or null when it names no email. */
type EmailUpdateListener = (emailId: number | null) => void;

const listeners = new Set<EmailUpdateListener>();
const openListeners = new Set<() => void>();

/** Deliver one `email_updated` event to every current subscriber. */
export function publishEmailUpdate(emailId: number | null): void {
  for (const listener of [...listeners]) listener(emailId);
}

/** Listen for `email_updated` events; returns the unsubscribe function. */
export function subscribeEmailUpdates(listener: EmailUpdateListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Announce that the app-wide stream has just (re)opened. Events published
 * while it was down were never delivered, so a subscriber that must not miss
 * any (the inbox) catches up on this signal instead of polling on a timer.
 */
export function publishStreamOpened(): void {
  for (const listener of [...openListeners]) listener();
}

/** Listen for stream (re)opens; returns the unsubscribe function. */
export function subscribeStreamOpened(listener: () => void): () => void {
  openListeners.add(listener);
  return () => {
    openListeners.delete(listener);
  };
}

/**
 * A leading-edge gate: true at most once per `windowMs`, so a burst of events
 * during batch processing triggers one refetch instead of dozens.
 */
export function createDebounceGate(windowMs: number): () => boolean {
  let last = Number.NEGATIVE_INFINITY;
  return () => {
    const now = Date.now();
    if (now - last < windowMs) return false;
    last = now;
    return true;
  };
}

interface UseEmailUpdatesOptions {
  /** Whether this consumer listens at all. The shared connection is unaffected. */
  enabled: boolean;
  /** Called (debounced per consumer) on email_updated. Used to trigger incremental polling. */
  onEmailUpdated?: (() => void) | undefined;
  /** Called per event with the updated email's id (not debounced). Used to refetch a visible row. */
  onEmailUpdatedById?: ((emailId: number) => void) | undefined;
  /**
   * Called each time the app-wide stream (re)opens — once per open, never
   * replayed to a late subscriber. Used to catch up on events missed while the
   * stream was down.
   */
  onStreamOpened?: (() => void) | undefined;
}

/**
 * Subscribe a view to the app-wide email stream.
 *
 * Each consumer gets its own debounce, so one view's refetch never swallows
 * another's. The subscription is keyed to `enabled` alone and reads the
 * callbacks through refs, so a re-render never resubscribes; an `<Activity>`
 * re-show re-runs the effect, which simply subscribes afresh.
 *
 * @returns `isConnected` — whether the app-wide stream is live, for choosing
 *   a polling fallback cadence.
 */
export function useEmailUpdates({
  enabled,
  onEmailUpdated,
  onEmailUpdatedById,
  onStreamOpened,
}: UseEmailUpdatesOptions): { isConnected: boolean } {
  const onEmailUpdatedRef = useRef(onEmailUpdated);
  const onEmailUpdatedByIdRef = useRef(onEmailUpdatedById);
  const onStreamOpenedRef = useRef(onStreamOpened);
  useEffect(() => {
    onEmailUpdatedRef.current = onEmailUpdated;
    onEmailUpdatedByIdRef.current = onEmailUpdatedById;
    onStreamOpenedRef.current = onStreamOpened;
  }, [onEmailUpdated, onEmailUpdatedById, onStreamOpened]);

  useEffect(() => {
    if (!enabled) return undefined;
    const pass = createDebounceGate(EMAIL_UPDATE_DEBOUNCE_MS);
    const unsubscribeUpdates = subscribeEmailUpdates(emailId => {
      if (emailId !== null) onEmailUpdatedByIdRef.current?.(emailId);
      if (pass()) onEmailUpdatedRef.current?.();
    });
    const unsubscribeOpens = subscribeStreamOpened(() => onStreamOpenedRef.current?.());
    return () => {
      unsubscribeUpdates();
      unsubscribeOpens();
    };
  }, [enabled]);

  return { isConnected: useAppStore(state => state.sseConnected) };
}
